// @ts-nocheck
import React, { useState, useEffect } from 'react'
import { Search, Filter, Save, X, Trash2 } from 'lucide-react'
import { useApi } from '../hooks/useApi'

export function AdvancedSearchPanel({ onSearch }) {
  const api = useApi()
  const [isExpanded, setIsExpanded] = useState(false)
  const [conditions, setConditions] = useState({
    priority: '',
    customerType: '',
    tags: '',
    minCallCount: '',
    maxCallCount: '',
    lastCallFrom: '',
    lastCallTo: '',
    createdFrom: '',
    createdTo: ''
  })

  const [savedFilters, setSavedFilters] = useState([])
  const [filterName, setFilterName] = useState('')
  const [showSaveDialog, setShowSaveDialog] = useState(false)

  useEffect(() => {
    if (isExpanded) {
      loadSavedFilters()
    }
  }, [isExpanded])

  async function loadSavedFilters() {
    try {
      const data = await api.get('/api/customers/filters')
      setSavedFilters(data.filters || [])
    } catch (error) {
      console.error('フィルタ読み込みエラー:', error)
    }
  }

  function handleSearch() {
    // 空の条件を除外
    const activeConditions = Object.entries(conditions).reduce((acc, [key, value]) => {
      if (value) acc[key] = value
      return acc
    }, {})
    
    onSearch(activeConditions)
  }

  function handleReset() {
    setConditions({
      priority: '',
      customerType: '',
      tags: '',
      minCallCount: '',
      maxCallCount: '',
      lastCallFrom: '',
      lastCallTo: '',
      createdFrom: '',
      createdTo: ''
    })
    onSearch({})
  }

  async function handleSaveFilter() {
    if (!filterName.trim()) {
      alert('フィルタ名を入力してください')
      return
    }

    try {
      await api.post('/api/customers/filters', {
        name: filterName,
        conditions
      })
      
      alert('フィルタを保存しました')
      setFilterName('')
      setShowSaveDialog(false)
      loadSavedFilters()
    } catch (error) {
      console.error('フィルタ保存エラー:', error)
      alert('フィルタの保存に失敗しました')
    }
  }

  function applyFilter(filter) {
    setConditions(filter.conditions)
    onSearch(filter.conditions)
  }

  async function handleDeleteFilter(filterId, e) {
    e.stopPropagation()
    
    if (!confirm('このフィルタを削除しますか？')) {
      return
    }

    try {
      await api.delete(`/api/customers/filters/${filterId}`)
      loadSavedFilters()
    } catch (error) {
      console.error('フィルタ削除エラー:', error)
      alert('フィルタの削除に失敗しました')
    }
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-4">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <Filter size={20} />
          <span className="font-medium">高度な検索</span>
          <span className="text-sm text-gray-500">
            {isExpanded ? '▲' : '▼'}
          </span>
        </button>

        {isExpanded && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowSaveDialog(true)}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
            >
              <Save size={16} />
              保存
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              <X size={16} />
              クリア
            </button>
          </div>
        )}
      </div>

      {isExpanded && (
        <>
          {/* 保存済みフィルタ */}
          {savedFilters.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                保存済みフィルタ
              </label>
              <div className="flex flex-wrap gap-2">
                {savedFilters.map(filter => (
                  <div
                    key={filter.filterId}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 cursor-pointer"
                  >
                    <button
                      onClick={() => applyFilter(filter)}
                      className="flex-1"
                    >
                      {filter.name}
                    </button>
                    <button
                      onClick={(e) => handleDeleteFilter(filter.filterId, e)}
                      className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 検索条件フォーム */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* 優先度 */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                優先度
              </label>
              <select
                value={conditions.priority}
                onChange={(e) => setConditions({ ...conditions, priority: e.target.value })}
                className="w-full border dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">すべて</option>
                <option value="vip">VIP</option>
                <option value="high">高</option>
                <option value="normal">普通</option>
                <option value="low">低</option>
              </select>
            </div>

            {/* 顧客タイプ */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                顧客タイプ
              </label>
              <select
                value={conditions.customerType}
                onChange={(e) => setConditions({ ...conditions, customerType: e.target.value })}
                className="w-full border dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">すべて</option>
                <option value="individual">個人</option>
                <option value="corporate">法人</option>
              </select>
            </div>

            {/* タグ */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                タグ（カンマ区切り）
              </label>
              <input
                type="text"
                value={conditions.tags}
                onChange={(e) => setConditions({ ...conditions, tags: e.target.value })}
                placeholder="VIP,法人"
                className="w-full border dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* 通話回数（最小） */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                最小通話回数
              </label>
              <input
                type="number"
                value={conditions.minCallCount}
                onChange={(e) => setConditions({ ...conditions, minCallCount: e.target.value })}
                className="w-full border dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* 通話回数（最大） */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                最大通話回数
              </label>
              <input
                type="number"
                value={conditions.maxCallCount}
                onChange={(e) => setConditions({ ...conditions, maxCallCount: e.target.value })}
                className="w-full border dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* 最終通話日（開始） */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                最終通話日（開始）
              </label>
              <input
                type="date"
                value={conditions.lastCallFrom}
                onChange={(e) => setConditions({ ...conditions, lastCallFrom: e.target.value })}
                className="w-full border dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* 最終通話日（終了） */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                最終通話日（終了）
              </label>
              <input
                type="date"
                value={conditions.lastCallTo}
                onChange={(e) => setConditions({ ...conditions, lastCallTo: e.target.value })}
                className="w-full border dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* 作成日（開始） */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                作成日（開始）
              </label>
              <input
                type="date"
                value={conditions.createdFrom}
                onChange={(e) => setConditions({ ...conditions, createdFrom: e.target.value })}
                className="w-full border dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* 作成日（終了） */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                作成日（終了）
              </label>
              <input
                type="date"
                value={conditions.createdTo}
                onChange={(e) => setConditions({ ...conditions, createdTo: e.target.value })}
                className="w-full border dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={handleSearch}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Search size={20} />
              検索
            </button>
          </div>
        </>
      )}

      {/* フィルタ保存ダイアログ */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
              フィルタを保存
            </h3>
            <input
              type="text"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="フィルタ名を入力"
              className="w-full border dark:border-gray-600 rounded px-3 py-2 mb-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowSaveDialog(false)
                  setFilterName('')
                }}
                className="px-4 py-2 border dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                キャンセル
              </button>
              <button
                onClick={handleSaveFilter}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
